import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { ReturingCustomersLayoutContext } from '../ReturningCustomersLayout';
import TreatmentSelection from './TreatmentSelection';
import RadioSelect from '../../../../components/RadioSelect/RadioSelect';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import ProductAvailabilityTag from './ProductAvailabilityTag';
import * as translator from "../../../../utils/translator"
import { useCartContext } from '../../../../customHooks/useCart';
import LabelCatalogTag from '../../FreeDosageChooseTreatmentV2/components/LabelCatalogTag';
import { LabelData } from '../../FreeDosageChooseTreatmentV2/FreeDosageLayoutV2';
import { displayedMedicationPrice } from '../../../../utils/helpers';

const COUNTRIES_ALLOWING_PO_DESIGN = ['FR', 'CH'];


interface ProductCardProps {
  productCatalogID: number,
  title: string,
  labelData: LabelData | null
}

const ProductCard: React.FC<ProductCardProps> = ({ productCatalogID, title,labelData }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const context = useContext(ReturingCustomersLayoutContext);
  const { cartCurrency } = useCartContext();
  const groupedProducts = context?.groupedProducts;
  const isSelected = useMemo(() => context?.selectedCatalogID === productCatalogID, [context?.selectedCatalogID]);
  const isOutOfStock = useMemo(() => (groupedProducts && groupedProducts[productCatalogID].every((product) => product.items.every((item) => !item.in_stock))), [groupedProducts, productCatalogID]);
  const isPreOrderable = !!(groupedProducts && groupedProducts[productCatalogID][0]?.is_pre_orderable);
  const disabled = isOutOfStock && !context?.allowPrescriptionOnlyFlow && !isPreOrderable;
  const flattenedProductItems = useMemo(() => groupedProducts && groupedProducts[productCatalogID].flatMap((product) => product.items), [groupedProducts, productCatalogID]);

  useEffect(() => {
    if(isSelected){
        if (cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth',block: "end"});
        }
    }
  },[cardRef]);

  const startingPrice = useMemo(() => {
    const items = isPreOrderable
      ? flattenedProductItems
      : flattenedProductItems?.filter((productItem) => productItem.in_stock);

    const displayedPriceComponents = context?.displayedPriceComponents;
    // Setting present: helper; else keep FR all-in / everyone-else official+service.
    if (displayedPriceComponents) {
      return Math.min(...items?.map((productItem) => displayedMedicationPrice(productItem, displayedPriceComponents)) || []) || 0
    }
    if (context?.countryID.toUpperCase() == "FR") {
      return Math.min(...items?.map((productItem) => productItem.price) || []) || 0
    }
    return Math.min(...items?.map((productItem) => (productItem.official_price + productItem.service_fee)) || []) || 0
  }, [flattenedProductItems, isPreOrderable, context?.displayedPriceComponents])
  const productItemToPlan = context?.productItemToPlan;

  const onSelect = useCallback(() => {
    if (groupedProducts && context?.selectedCatalogID !== productCatalogID) {
      context?.setSelectedCatalogID(productCatalogID);
      const firstInStockProduct = context?.findByDefaultDosage(groupedProducts[productCatalogID]) || groupedProducts[productCatalogID].find((product) => product.in_stock || context?.allowPrescriptionOnlyFlow) || groupedProducts[productCatalogID][0];

      MainTracker.superProperties({ "Product Name": firstInStockProduct!.title });
      window.eventsTracking.track({ e: "Click change Product Name" });
      // Track expand event when selecting (expanding) the card
      window.eventsTracking.track({ 
        e: "product_card_expanded",
      });
      context?.setSelectedProductID(firstInStockProduct!.product_id);
      const firstInStockProductItem = context?.findQuantityBasedOnRule(firstInStockProduct!) || firstInStockProduct!.items.find((item) => item.in_stock || context?.allowPrescriptionOnlyFlow) || firstInStockProduct!.items[0];

      context?.setSelectedProductItemID(firstInStockProductItem!.product_item_id);
      context?.setIsExpressProductItem(firstInStockProductItem?.express_stock || false);
      context?.setIsNoPreferenceSelected(false);
      if (productItemToPlan) {
        const plans = productItemToPlan[firstInStockProductItem!.product_item_id];
        if (plans) {
          context?.setSelectedSubscriptionPlanID(plans[0]);
        } else {
          context?.setSelectedSubscriptionPlanID(null);
        }
      }
    }
    // scrollToTop();
  }, [groupedProducts, context, productCatalogID, productItemToPlan]);

  // ---- Not sure we even want this, its nice on mobile, but weird on desktop :/ -----
  // const scrollToTop = () => {
  //   setTimeout(() => {
  //     if (cardRef.current) {
  //       cardRef.current.scrollIntoView({ behavior: 'smooth', block: "start" });
  //     }
  //   }, 200)
  // }

  const bgColor = useMemo(() => {
    if (disabled) return "bg-[#f0eded]"
    if (isSelected) return "bg-primary-lightgreen-surface"
    return "bg-white"
  }, [disabled, isSelected])

  return (
    <div ref={cardRef} id={`catalog_${productCatalogID}`} onClick={disabled ? () => { } : onSelect} style={{ border: (isSelected && !disabled) ? "1px solid #269C6E" : "" }} className={`${bgColor} scroll-mt-[20px] flex flex-col items-center rounded-md ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      <div className={`flex px-4 select-none gap-2 w-full py-5 relative scroll-mt-[100px] md:mr-2  md:gap-2 p-4 pt-6 md:p-6 rounded-[8px]`}>
        
        <div className='flex flex-1 min-w-0 items-center '>
          <RadioSelect size={20} selected={isSelected && !disabled} disabled={disabled || false} />
          <p className='text-[#0d2c54] text-sm font-medium'>{title}</p>
          {isPreOrderable && (
            <span className="ml-2 bg-[#61a333] text-white text-[12px] font-normal px-2 py-0.5 rounded-md whitespace-nowrap">
              {translator.translate(context?.languageID, 'choose_treatment', 'pre_order_badge')}
            </span>
          )}
          {labelData&&<LabelCatalogTag labelData={labelData} className='absolute -top-2 right-6' />}
        </div>

        <div className={`from-price ${(isSelected && !disabled) ? "text-[#0d2c54]" : "text-[#808080]"} text-sm font-normal text-nowrap flex gap-1 items-center`}>
          {
            context?.hidePrices || (COUNTRIES_ALLOWING_PO_DESIGN.includes(context?.countryID?.toUpperCase() || '') && isOutOfStock && !isPreOrderable)
              ? null
              : (isOutOfStock && !isPreOrderable)
                ? <span>{translator.translate(context?.languageID, "default", "ChooseTreatment/outOfStock")}</span>
                : (
                    <>
                      <span className='text-xs font-normal'>
                        {translator.translate(
                          context?.languageID,
                          "default",
                          "ChooseTreatment/from"
                        )}
                      </span>
                      <span>
                        {translator.getPrice(startingPrice, cartCurrency.locale, cartCurrency.rate)}
                      </span>
                    </>
                  )
          }
        </div>

        {
          COUNTRIES_ALLOWING_PO_DESIGN.includes(context?.countryID?.toUpperCase() || '') ? <ProductAvailabilityTag inStock={!isOutOfStock} /> : null
        }   

      </div>

      <div id='hidden-content' className='md:hidden w-full grow-anim'>
        {
          isSelected && <TreatmentSelection isResponsiveLayout />
        }
      </div>
    </div>
  )
}

export default ProductCard
